'use client';

import { useTranslations } from 'next-intl';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '@/shared/ui/shadcn/select';
import { cn } from '@/shared/lib/cn';
import { resolveUnitLabel, type UnitOption } from '../api/estimate-service';

/** Radix Select forbids an empty-string item value — sentinel for "no unit". */
const NONE_VALUE = '__none__';

interface EstimateUnitSelectProps {
  /** Current display unit code (server-denominated), null = no unit. */
  value: string | null;
  /** Server-driven options for the estimate's current measurement system. */
  options: UnitOption[];
  disabled?: boolean;
  /** A discrete unit pick — commits immediately (no debounce). */
  onChange: (unitCode: string | null) => void;
}

/**
 * Inline unit dropdown for an estimate item row. The trigger is borderless and
 * matches the row's other inline fields (no chip chrome — the row is a dense
 * figures line); options come from the server-driven `unit_options` (localized
 * labels, current measurement system) plus an em-dash "no unit" entry. A code
 * missing from the options (legacy data) still renders as the raw code.
 */
export function EstimateUnitSelect({
  value,
  options,
  disabled = false,
  onChange,
}: EstimateUnitSelectProps) {
  const t = useTranslations('common.estimate.editor');

  const selectValue = value === null || value === '' ? NONE_VALUE : value;

  return (
    <Select
      value={selectValue}
      onValueChange={(next) => {
        const normalized = next === NONE_VALUE ? null : next;
        if (normalized !== (value ?? null)) onChange(normalized);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={t('selectUnit')}
        className={cn(
          // Match the row's borderless inline-field look — the chevron is the
          // only "this is a control" affordance.
          'h-auto w-auto gap-0.5 rounded-none border-none bg-transparent p-0 text-card-meta shadow-none focus-visible:ring-0',
          value ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {resolveUnitLabel(options, value)}
      </SelectTrigger>
      <SelectContent align="start">
        <SelectGroup>
          <SelectItem value={NONE_VALUE} aria-label={t('unitNone')}>
            —
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option.code} value={option.code}>
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
